/**
 * Kleines, konsistentes Icon-Set — Strichzeichnungen statt Emoji.
 *
 * Emoji sehen je nach Betriebssystem unterschiedlich aus und wirken schnell
 * nach Spielzeug-App. Ein einheitliches Strich-Set (wie es jede ernstzunehmende
 * App benutzt) macht den Unterschied zwischen "Prototyp" und "Produkt".
 * Alle Icons: 24×24 Raster, currentColor, 2px Strichstärke, runde Enden.
 */
import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

function Basis({ children, ...props }: IconProps & { children: React.ReactNode }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden="true"
			{...props}
		>
			{children}
		</svg>
	);
}

export function MikrofonIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<rect x="9" y="2" width="6" height="12" rx="3" />
			<path d="M5 10a7 7 0 0 0 14 0" />
			<line x1="12" y1="19" x2="12" y2="22" />
			<line x1="8" y1="22" x2="16" y2="22" />
		</Basis>
	);
}

export function KameraIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<path d="M4 8a2 2 0 0 1 2-2h2l1.2-1.6A2 2 0 0 1 10.8 3.6h2.4a2 2 0 0 1 1.6.8L16 6h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z" />
			<circle cx="12" cy="13" r="3.5" />
		</Basis>
	);
}

export function HakenIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<polyline points="4 12.5 9.5 18 20 6" />
		</Basis>
	);
}

export function PfeilLinksIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<polyline points="14 5 7 12 14 19" />
			<line x1="7" y1="12" x2="20" y2="12" />
		</Basis>
	);
}

export function PfeilRechtsIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<polyline points="10 5 17 12 10 19" />
			<line x1="4" y1="12" x2="17" y2="12" />
		</Basis>
	);
}

export function PlusIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<line x1="12" y1="5" x2="12" y2="19" />
			<line x1="5" y1="12" x2="19" y2="12" />
		</Basis>
	);
}

export function SchluesselIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<circle cx="7.5" cy="15.5" r="4.5" />
			<path d="M10.8 12.2 20 3" />
			<path d="M16 7l3 3" />
			<path d="M13.5 9.5l2.5 2.5" />
		</Basis>
	);
}

export function ZaehlerIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<circle cx="12" cy="13" r="8" />
			<path d="M12 13 15.5 9" />
			<path d="M12 5v1.5" />
			<path d="M9 20.5 12 21l3-.5" />
		</Basis>
	);
}

export function UnterschriftIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<path d="M3 17c2-1 3-3 4-5.5S8.5 6 10 6s1 3-.5 6.5S8 18.5 10 19s4-2 6-5 3.5-4.5 5-3.5" />
			<line x1="3" y1="21" x2="21" y2="21" />
		</Basis>
	);
}

export function DokumentIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<path d="M7 3h7l4 4v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1Z" />
			<polyline points="14 3 14 8 19 8" />
			<line x1="9" y1="13" x2="15" y2="13" />
			<line x1="9" y1="17" x2="15" y2="17" />
		</Basis>
	);
}

export function TeilenIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<circle cx="18" cy="5" r="2.5" />
			<circle cx="6" cy="12" r="2.5" />
			<circle cx="18" cy="19" r="2.5" />
			<path d="m8.2 10.8 7.6-4.5" />
			<path d="m8.2 13.2 7.6 4.5" />
		</Basis>
	);
}

export function GeraetSpeicherIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<path d="M5 3h12l2 2v16H5Z" />
			<path d="M8 3v6h8V3" />
			<rect x="8" y="14" width="8" height="7" rx="1" />
		</Basis>
	);
}

export function MuellIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<polyline points="4 7 20 7" />
			<path d="M6 7V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v2" />
			<path d="M8 7v13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V7" />
			<line x1="10" y1="11" x2="10" y2="17" />
			<line x1="14" y1="11" x2="14" y2="17" />
		</Basis>
	);
}

export function StiftIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<path d="m4 20 4.5-1 10-10a2.1 2.1 0 0 0-3-3l-10 10Z" />
			<path d="m14 7 3 3" />
		</Basis>
	);
}

export function RueckgaengigIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<path d="M9 7 4 12l5 5" />
			<path d="M5 12h8a7 7 0 0 1 7 7" />
		</Basis>
	);
}

export function SchliessenIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<line x1="6" y1="6" x2="18" y2="18" />
			<line x1="18" y1="6" x2="6" y2="18" />
		</Basis>
	);
}

export function HausIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<path d="M4 11.5 12 4l8 7.5" />
			<path d="M6 10v9a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1v-9" />
			<line x1="10" y1="20" x2="10" y2="14" />
			<line x1="14" y1="20" x2="14" y2="14" />
		</Basis>
	);
}

export function WarnungIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<path d="M12 3 22 20H2Z" />
			<line x1="12" y1="9" x2="12" y2="14" />
			<line x1="12" y1="17" x2="12" y2="17.01" />
		</Basis>
	);
}

export function DownloadIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<path d="M12 4v11" />
			<path d="M7.5 11 12 15.5 16.5 11" />
			<path d="M5 19h14" />
		</Basis>
	);
}

export function UploadIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<path d="M12 19V8" />
			<path d="M7.5 12 12 7.5 16.5 12" />
			<path d="M5 19h14" />
		</Basis>
	);
}

export function VergleichIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<path d="M9 4v16" />
			<path d="M15 4v16" />
			<path d="M4 9h3" />
			<path d="M4 15h3" />
			<path d="M17 9h3" />
			<path d="M17 15h3" />
		</Basis>
	);
}

export function OrdnerIcon(props: IconProps) {
	return (
		<Basis {...props}>
			<path d="M4 7a1 1 0 0 1 1-1h4l2 2h8a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1Z" />
		</Basis>
	);
}
